import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { continueTo } from "../lib/continueTo";
import { exchangeCredential, loadGoogleIdentity } from "../lib/google";

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * What to put on the screen when signing in did not work.
 *
 * `userMessage` rather than the error's own message, which is composed as
 * "Forbidden: ...": the title is the HTTP status spelled out and means nothing
 * to somebody who just wants to know why they are not in. The detail is the
 * sentence the API wrote for them (ADR 000109).
 */
function describe(cause: unknown): string {
  if (cause instanceof ApiError) return cause.userMessage;
  return cause instanceof Error ? cause.message : "Sign-in failed";
}

/**
 * The sign-in screen.
 *
 * `ended` when somebody was signed in a moment ago and their session has since
 * gone, which is the one way onto this screen that nobody asked for: it happens
 * mid-edit, on the screen they were working on (ADR 000131).
 */
export function SignInRoute({ ended }: { ended: boolean }) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!CLIENT_ID) return;

    let cancelled = false;

    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !buttonRef.current) return;

        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          cancel_on_tap_outside: false,
          callback: (response) => {
            exchangeCredential(response.credential)
              .then(async () => {
                // Something sent this person here to sign in and wants them
                // back: the OAuth consent screen does, because studio owns
                // sign-in and the authorization server does not (ADR 000043).
                const next = continueTo(window.location.href);
                if (next) {
                  window.location.replace(next);
                  return;
                }

                /**
                 * Nothing cached belongs to whoever just signed in. Until a
                 * session ending sent somebody here without a reload this was
                 * a reload's job, and `signOut` clears for the same reason
                 * (ADR 000131).
                 *
                 * The session query survives it, because invalidating a query
                 * that has been removed refetches nothing and this screen is
                 * waiting on that refetch. Everything else is unmounted behind
                 * this card, so dropping it is silent.
                 */
                queryClient.removeQueries({
                  predicate: (query) => query.queryKey[0] !== "session",
                });
                await queryClient.invalidateQueries({ queryKey: ["session"] });
              })
              .catch((cause: unknown) => {
                setError(describe(cause));
              });
          },
        });

        google.accounts.id.renderButton(buttonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
        });
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "Sign-in unavailable");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  return (
    <main className="signin">
      <div className="signin__card">
        <h1>scenix</h1>
        <p className="signin__lede">Sign in to reach your spaces.</p>

        {ended && (
          /* Announced, rather than left to be found: this screen replaced the
             one somebody was working on, which is a change they did not make. */
          <p className="signin__notice" role="status">
            Your session ended, so this browser was signed out. Signing in again picks up from your
            spaces; anything you had not saved is not here.
          </p>
        )}

        {CLIENT_ID ? (
          <div ref={buttonRef} className="signin__button" />
        ) : (
          <p className="signin__notice">
            <code>VITE_GOOGLE_CLIENT_ID</code> is not set, so the Google button cannot render. Add
            it to <code>.env</code> and restart the dev server — see the README.
          </p>
        )}

        {error && (
          <p className="signin__error" role="alert">
            {error}
          </p>
        )}

        <p className="signin__footnote">
          Google is the only way in. scenix stores no passwords (ADR 000006).
        </p>
      </div>
    </main>
  );
}
